<script setup>
import { computed, ref, nextTick, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import { usePolicy } from 'dashboard/composables/usePolicy';

const emit = defineEmits(['add', 'import', 'export']);

const { t } = useI18n();
const router = useRouter();
const { checkPermissions } = usePolicy();

const contactMenuItems = computed(() => [
  {
    label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.ADD_CONTACT'),
    action: 'add',
    value: 'add',
    icon: 'i-ph-plus',
  },
  ...(checkPermissions(['administrator', 'contact_manage'])
    ? [
        {
          label: t(
            'CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.EXPORT_CONTACT'
          ),
          action: 'export',
          value: 'export',
          icon: 'i-ph-upload-simple',
        },
      ]
    : []),
  ...(checkPermissions(['administrator', 'contact_manage'])
    ? [
        {
          label: t(
            'CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.IMPORT_CONTACT'
          ),
          action: 'import',
          value: 'import',
          icon: 'i-ph-download-simple',
        },
      ]
    : []),
  // SiteUp: a revisão de duplicatas do WhatsApp só abria por URL direta.
  {
    label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.REVIEW_WAHA_DUPLICATES'),
    action: 'wahaDuplicates',
    value: 'wahaDuplicates',
    icon: 'i-ri-whatsapp-line',
  },
]);
const showActionsDropdown = ref(false);
const root = ref(null);
const actionsId = useId();
const closeActions = () => {
  showActionsDropdown.value = false;
  root.value?.querySelector('button')?.focus();
};
const toggleActions = async () => {
  showActionsDropdown.value = !showActionsDropdown.value;
  if (showActionsDropdown.value) {
    await nextTick();
    document.getElementById(actionsId)?.querySelector('button')?.focus();
  }
};

const handleContactAction = ({ action }) => {
  if (!contactMenuItems.value.some(item => item.action === action)) return;
  closeActions();
  if (action === 'add') {
    emit('add');
  } else if (action === 'import') {
    emit('import');
  } else if (action === 'export') {
    emit('export');
  } else if (action === 'wahaDuplicates') {
    router.push({ name: 'contacts_waha_duplicates' });
  }
};
</script>

<template>
  <div
    ref="root"
    v-on-clickaway="() => (showActionsDropdown = false)"
    class="relative"
    @keydown.esc.stop.prevent="closeActions"
  >
    <Button
      icon="i-ph-dots-three-vertical"
      :aria-label="t('CONTACTS_LAYOUT.HEADER.ACTIONS.MORE_ACTIONS')"
      :aria-expanded="showActionsDropdown"
      :aria-controls="showActionsDropdown ? actionsId : undefined"
      class="!h-11 !w-11 !rounded-xl"
      color="slate"
      variant="ghost"
      size="sm"
      :class="showActionsDropdown ? 'bg-n-alpha-2' : ''"
      @click="toggleActions"
    />
    <DropdownMenu
      v-if="showActionsDropdown"
      :id="actionsId"
      :menu-items="contactMenuItems"
      class="ltr:right-0 rtl:left-0 mt-1 w-52 top-full"
      @action="handleContactAction($event)"
    />
  </div>
</template>
